import type { ComponentType, ElementType, SVGAttributes } from "react";

import { useFlags } from "launchdarkly-react-client-sdk";

import {
  AgentsIcon,
  AnalyticsIcon,
  ApproveIcon,
  AssetsIcon,
  CalendarDynamicIcon,
  ChatIcon,
  FlagIcon,
  LaunchAdIcon,
  LightningBoltIcon,
  SequencesIcon,
  SettingsIcon,
  ShoppingBagIcon,
} from "@hightouch/htui";

import {
  AGENT_CHATS_PATH,
  useShowAgentsInNav,
} from "src/components/layout/nav/sections/hooks/agents";
import { Icon as ContextIcon } from "src/components/layout/nav/icons/context";
import { useContextHomePath } from "src/components/layout/nav/sections/hooks/context";
import { useHasProductTabs } from "src/components/layout/nav/sections/hooks/projects";
import { useSettingsHomePath } from "src/components/layout/nav/sections/hooks/settings";
import { useResourcePermission } from "src/components/permission/use-resource-permission";
import { useSpaceContext } from "src/components/spaces";
import {
  type AdStudioProductFeedCountQuery,
  useAdStudioProductFeedCountQuery,
} from "src/graphql";
import { ACTIONS_PATHS } from "src/pages/agents/actions/constants";
import {
  CALENDAR_PATH,
  useCalendarReadyNotice,
} from "src/pages/agents/calendar/setup/calendar-ready-notice";
import { useAgentsEnabledFlag } from "src/pages/agents/hooks/use-agents-enabled-flag";
import { useIsAdStudioEnabled } from "src/pages/agents/hooks/use-is-ad-studio-enabled";
import { useIsAdStudioLaunchEnabled } from "src/pages/ad-studio/hooks/use-is-ad-studio-launch-enabled";
import {
  PROJECTS_PATHS,
  PROJECTS_TERMS as TERMS,
} from "src/pages/projects/constants";

export type AmpNavLink = {
  href: string;
  icon: ComponentType<SVGAttributes<SVGElement>>;
  label: string;
  /** Dots the icon; the label is what the dot means. */
  markerLabel?: string;
  /** Other paths that light this link up, as `/:section` or `/:section/:page`. */
  activePaths?: string[];
};

/** Context's link: it takes the nav's own Context icon, which, unlike a UI-library icon, isn't a crumb icon. */
export type AmpContextLink = Omit<AmpNavLink, "icon"> & { icon: ElementType };

const HOME_LINK: AmpNavLink = {
  href: AGENT_CHATS_PATH,
  icon: ChatIcon,
  label: "Chats",
};
const AREA_LINKS: AmpNavLink[] = [
  { href: PROJECTS_PATHS.tasks, icon: ApproveIcon, label: TERMS.task.Plural },
  { href: PROJECTS_PATHS.library, icon: AssetsIcon, label: "Library" },
];
/** What the product nav calls the Library, which holds ads alongside canvases and the other files. */
export const AMP_CONTENT_LABEL = "Content";
// Agent reports, which the AMP nav calls routines.
export const AMP_ROUTINES_PATH = "/agents/reports";
const ROUTINES_LINK: AmpNavLink = {
  href: AMP_ROUTINES_PATH,
  icon: SequencesIcon,
  label: "Routines",
};
const ACTIONS_LINK: AmpNavLink = {
  href: ACTIONS_PATHS.list,
  icon: LightningBoltIcon,
  label: "Actions",
};
const CALENDAR_LINK: AmpNavLink = {
  href: CALENDAR_PATH,
  icon: CalendarDynamicIcon,
  label: "Calendar",
};
export const AMP_INITIATIVES_PATH = "/agents/initiatives";
const INITIATIVES_LINK: AmpNavLink = {
  href: AMP_INITIATIVES_PATH,
  icon: FlagIcon,
  label: "Initiatives",
};
// Users not flagged into agents get the one entry to the agents splash page.
const AGENTS_SPLASH_LINK: AmpNavLink = {
  href: "/agents",
  icon: AgentsIcon,
  label: "Agents",
};
export const AMP_ADS_SECTION_PATH = "/ad-studio";
/** Ad Studio's new chat, which starts an ad. */
export const AMP_NEW_AD_CHAT_PATH = `${AMP_ADS_SECTION_PATH}/projects`;
export const AMP_AD_CHATS_PATH = `${AMP_ADS_SECTION_PATH}/chats`;
export const AMP_INSIGHTS_PATH = "/ad-studio/insights";
export const AMP_CATALOG_ADS_PATH = "/ad-studio/catalog-ads";
/** Ad Studio's launch queue and launched ads, which the Automations panel lists. */
export const AMP_LAUNCH_PATH = `${AMP_ADS_SECTION_PATH}/launch`;
const LAUNCH_LINK: AmpNavLink = {
  href: AMP_LAUNCH_PATH,
  icon: LaunchAdIcon,
  label: "Launch",
};
const INSIGHTS_LINK: AmpNavLink = {
  href: AMP_INSIGHTS_PATH,
  icon: AnalyticsIcon,
  label: "Insights",
};
const CATALOG_ADS_LINK: AmpNavLink = {
  href: AMP_CATALOG_ADS_PATH,
  icon: ShoppingBagIcon,
  label: "Catalog ads",
};
const CALENDAR_READY_LABEL = "Calendar is ready";

const useIsReachable = () => {
  const { isInSpace, isPathOrChildrenAccessibleInSpace } = useSpaceContext();
  return (link: Pick<AmpNavLink, "href">) =>
    !isInSpace || isPathOrChildrenAccessibleInSpace(link.href);
};

/** Calendar and Initiatives, for viewers flagged into agents and each page's own flag. */
const useLifecycleLinks = (): AmpNavLink[] => {
  const showAgents = useShowAgentsInNav();
  const isAgentsEnabled = useAgentsEnabledFlag();
  const { campaignCalendar, agenticInitiatives } = useFlags();
  const isCalendarReady = useCalendarReadyNotice();
  const isReachable = useIsReachable();

  if (!showAgents || !isAgentsEnabled) {
    return [];
  }
  return [
    ...(campaignCalendar
      ? [
          isCalendarReady
            ? { ...CALENDAR_LINK, markerLabel: CALENDAR_READY_LABEL }
            : CALENDAR_LINK,
        ]
      : []),
    ...(agenticInitiatives ? [INITIATIVES_LINK] : []),
  ].filter(isReachable);
};

/** Whether the AMP nav shows Ad Studio's pages, and among them Insights. */
export const useAdStudioInAmpNav = () => {
  const hasProductTabs = useHasProductTabs();
  const isAdStudioEnabled = useIsAdStudioEnabled();
  const { isPermitted: canViewAgents } = useResourcePermission({
    v2: { resource: "workspace", grant: "can_view_agents" },
  });
  const isReachable = useIsReachable();
  const hasAdStudio = hasProductTabs && isAdStudioEnabled && canViewAgents;
  return {
    hasAdStudio,
    hasInsights: hasAdStudio && isReachable(INSIGHTS_LINK),
  };
};

/** Catalog ads, which the Library lists with the product tabs instead of the AMP nav. */
export function useLibraryCatalogAdsLink(): AmpNavLink | null {
  const { hasAdStudio } = useAdStudioInAmpNav();
  const { adStudioEnrichedCatalogs } = useFlags();
  const isReachable = useIsReachable();
  return hasAdStudio &&
    adStudioEnrichedCatalogs &&
    isReachable(CATALOG_ADS_LINK)
    ? CATALOG_ADS_LINK
    : null;
}

/** How many catalog ads there are, fetched only while the catalog ads link shows. */
export function useCatalogAdsCount(
  catalogAdsLink: AmpNavLink | null,
): number | undefined {
  return useAdStudioProductFeedCountQuery(undefined, {
    enabled: Boolean(catalogAdsLink),
    select: selectCatalogAdsCount,
  }).data;
}

function selectCatalogAdsCount(data: AdStudioProductFeedCountQuery) {
  return data.ad_studio_product_feeds_aggregate.aggregate?.count ?? 0;
}

/** AMP's pages in the order the AMP nav lists them, for that nav and the canvas's mini-nav alike. */
export function useAmpNavLinks(): {
  /** Leads the nav: Home, with the product tabs and the agent pages. */
  lead: AmpNavLink[];
  /** The area's own pages. */
  area: AmpNavLink[];
  /** After the area's pages, with the product tabs: the other agent pages, then Insights. */
  more: AmpNavLink[];
  /** Context, in the full-screen settings, with the product tabs. */
  context: AmpContextLink | null;
  /** Full-screen settings with the product tabs, otherwise the area's own. */
  settings: AmpNavLink | null;
} {
  const hasProductTabs = useHasProductTabs();
  const showAgents = useShowAgentsInNav();
  const isAgentsEnabled = useAgentsEnabledFlag();
  const settingsHomePath = useSettingsHomePath();
  const contextHomePath = useContextHomePath();
  const isReachable = useIsReachable();
  const lifecycleLinks = useLifecycleLinks();
  const { hasAdStudio, hasInsights } = useAdStudioInAmpNav();
  const catalogAdsLink = useLibraryCatalogAdsLink();
  const isLaunchEnabled = useIsAdStudioLaunchEnabled();
  const launchLinks =
    hasAdStudio && isLaunchEnabled ? [LAUNCH_LINK].filter(isReachable) : [];

  const hasAgentPages = hasProductTabs && showAgents;
  const agentLinks = !hasAgentPages
    ? []
    : !isAgentsEnabled
      ? [AGENTS_SPLASH_LINK]
      : [
          ...[ROUTINES_LINK, ACTIONS_LINK].filter(isReachable),
          ...lifecycleLinks,
        ];
  // With the product tabs the Library is Content, which opens on the Library's New view and lights up on every Library view and Catalog ads, which its sidebar lists.
  const areaLinks = AREA_LINKS.map((link) =>
    link.href !== PROJECTS_PATHS.library
      ? link
      : hasProductTabs
        ? {
            ...link,
            href: PROJECTS_PATHS.libraryNew,
            label: AMP_CONTENT_LABEL,
            activePaths: [
              PROJECTS_PATHS.library,
              ...(catalogAdsLink ? [catalogAdsLink.href] : []),
            ],
          }
        : link,
  );
  const lead =
    hasAgentPages && isAgentsEnabled ? [HOME_LINK].filter(isReachable) : [];
  const settingsHref = hasProductTabs
    ? settingsHomePath
    : PROJECTS_PATHS.settings;

  return {
    lead,
    area: areaLinks,
    more: [
      ...agentLinks,
      ...launchLinks,
      ...(hasInsights ? [INSIGHTS_LINK] : []),
    ],
    context:
      hasProductTabs && contextHomePath
        ? { href: contextHomePath, icon: ContextIcon, label: "Context" }
        : null,
    settings: settingsHref
      ? { href: settingsHref, icon: SettingsIcon, label: "Settings" }
      : null,
  };
}
